/** Prisma CUID-ish ids used across admin routes (tenant, registration, program, …). */
export const CUID_RE = /\bc[a-z0-9]{24,32}\b/gi

export function isLikelyCuid(value: string): boolean {
  return /^c[a-z0-9]{24,32}$/i.test(value.trim())
}

export type KnownCuidEntity = {
  id: string
  name: string
  href: string
}

export type KnownCuidMap = Record<string, KnownCuidEntity>

export function buildTenantCuidHref(tenantId: string): string {
  return `/tenant/tenants/${encodeURIComponent(tenantId)}`
}

export function buildOnboardingCuidHref(registrationId: string): string {
  return `/tenant/onboarding/${encodeURIComponent(registrationId)}`
}

export function buildProgramCuidHref(programId: string): string {
  return `/program/programs/${encodeURIComponent(programId)}`
}

export function buildTariffCuidHref(tariffId: string): string {
  return `/admin/tariffs/${encodeURIComponent(tariffId)}`
}

export function extractCuidFromAdminHref(href: string | undefined | null): string | null {
  if (!href) return null
  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    const patterns = [
      /^\/tenant\/tenants\/([^/?#]+)/,
      /^\/tenant\/onboarding\/([^/?#]+)/,
      /^\/program\/programs\/([^/?#]+)/,
      /^\/admin\/tariffs\/([^/?#]+)/,
    ]
    for (const pattern of patterns) {
      const match = url.pathname.match(pattern)
      const id = match?.[1] ? decodeURIComponent(match[1]) : null
      if (id && isLikelyCuid(id)) return id
    }
  } catch {
    // ignore
  }
  return null
}

/**
 * Turn known bare CUIDs into markdown links. Skips fenced/inline code and existing links.
 */
export function linkifyKnownCuidsInMarkdown(markdown: string, known: KnownCuidMap): string {
  const ids = Object.keys(known)
  if (ids.length === 0) return markdown

  const placeholders: string[] = []
  const protect = (chunk: string) => {
    const token = `\u0000CUID${placeholders.length}\u0000`
    placeholders.push(chunk)
    return token
  }

  let text = markdown
    .replace(/```[\s\S]*?```/g, protect)
    .replace(/`[^`\n]+`/g, protect)
    .replace(/\[[^\]]*]\([^)]+\)/g, protect)

  text = text.replace(CUID_RE, (id) => {
    const entity = known[id]
    if (!entity) return id
    return `[${id}](${entity.href})`
  })

  return text.replace(/\u0000CUID(\d+)\u0000/g, (_, idx) => placeholders[Number(idx)] ?? '')
}

/** Replace known CUID substrings with `id · name` for display (keeps unknown ids). */
export function expandCuidsInText(text: string, known: KnownCuidMap): string {
  if (Object.keys(known).length === 0) return text
  return text.replace(CUID_RE, (id) => {
    const entity = known[id]
    if (!entity?.name) return id
    return `${id} · ${entity.name}`
  })
}
